<script setup>
import { defineAsyncComponent, ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { useUpgradePage } from 'dashboard/composables/useUpgradePage';

import wootConstants from 'dashboard/constants/globals';

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';
import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const route = useRoute();
const router = useRouter();
const store = useStore();

watch(
  () => route.query,
  query => {
    if (query?.status === 'success' && query?.username) {
      const accId = route.params.accountId || store?.getters?.getCurrentAccountId || 1;
      router.replace({
        name: 'settings_inboxes_page_channel',
        params: {
          accountId: accId,
          sub_page: 'instagram',
        },
      });
    }
  },
  { immediate: true }
);
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const { shouldShowUpgradePage: isLimitExceeded } = useUpgradePage();

const showCreateAccountModal = ref(false);
const showShortcutModal = ref(false);
const isMobileSidebarOpen = ref(false);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

const callsStore = useCallsStore();
const hasActiveCall = computed(() => callsStore.hasActiveCall);
const hasIncomingCall = computed(() => callsStore.hasIncomingCall);

const bypassUpgradePage = computed(() => {
  return [
    'billing_settings_index',
    'settings_inbox_list',
    'general_settings_index',
    'agent_list',
  ].includes(route.name);
});

const showUpgradePage = computed(() => {
  if (bypassUpgradePage.value) return false;
  return isLimitExceeded.value;
});

const previouslyUsedDisplayType = computed(() => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    previously_used_conversation_display_type:
      conversationDisplayType = LAYOUT_TYPES.EXPANDED,
  } = uiSettings.value;
  return conversationDisplayType;
});

watch(
  isSmallScreen,
  () => {
    const { LAYOUT_TYPES } = wootConstants;
    if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
      updateUISettings({
        conversation_display_type: LAYOUT_TYPES.EXPANDED,
      });
    } else {
      updateUISettings({
        conversation_display_type: previouslyUsedDisplayType.value,
      });
    }
  },
  { immediate: true }
);

const toggleMobileSidebar = () => {
  isMobileSidebarOpen.value = !isMobileSidebarOpen.value;
};

const closeMobileSidebar = () => {
  isMobileSidebarOpen.value = false;
};

const openCreateAccountModal = () => {
  showCreateAccountModal.value = true;
};

const closeCreateAccountModal = () => {
  showCreateAccountModal.value = false;
};

const toggleKeyShortcutModal = () => {
  showShortcutModal.value = true;
};

const closeKeyShortcutModal = () => {
  showShortcutModal.value = false;
};
</script>

<template>
  <div
    class="flex flex-grow overflow-hidden text-foreground bg-background relative h-screen w-screen"
  >
    <!-- Premium Ambient Glows -->
    <div
      class="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-violet-600/10 dark:bg-violet-600/5 blur-[130px] pointer-events-none z-0 animate-pulse duration-[8s]"
    />
    <div
      class="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-indigo-600/10 dark:bg-indigo-600/5 blur-[130px] pointer-events-none z-0 animate-pulse duration-[10s]"
    />
    <div
      class="absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full bg-blue-500/5 dark:bg-blue-500/2 blur-[110px] pointer-events-none z-0"
    />

    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-1 h-[calc(100%-1.5rem)] my-3 ltr:ml-4 ltr:mr-3 rtl:mr-4 rtl:ml-3 rounded-2xl border border-n-weak dark:border-n-strong bg-n-surface-1/40 dark:bg-n-surface-1/20 backdrop-blur-xl min-h-0 overflow-hidden shadow-2xl relative z-10 transition-all duration-300 ease-in-out"
    >
      <UpgradePage
        v-if="showUpgradePage"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-else>
        <router-view />
        <CommandBar />
        <CopilotLauncher />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
